import { Text, Stack, Box, VisuallyHidden } from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import type { FormQuestion } from "@/components/features/FormBuilder/types/form.types";
import type { FormLogicContext } from "@/components/features/FormBuilder/data/contextTriggers";
import { optionLabel } from "@/components/features/FormBuilder/data/builderLabels";
import { getUnitPlacement } from "@/components/features/FormBuilder/utils/currencyUnit";
import { isQuestionRequired } from "@/components/features/FormBuilder/utils/evaluateLogic";
import { ControlledAcknowledgement } from "@/components/features/FormBuilder/components/Wrappers/ControlledAcknowledgement";
import { ControlledCheckbox } from "@/components/features/FormBuilder/components/Wrappers/ControlledCheckbox";
import { ControlledDatePicker } from "@/components/features/FormBuilder/components/Wrappers/ControlledDatePicker";
import { ControlledInput } from "@/components/features/FormBuilder/components/Wrappers/ControlledInput";
import { ControlledRepeatingGroup } from "@/components/features/FormBuilder/components/Wrappers/ControlledRepeatingGroup";
import { ControlledMediaCapture } from "@/components/features/FormBuilder/components/Wrappers/MediaCaptureField/MediaCaptureField";
import { ControlledMultiSelect } from "@/components/features/FormBuilder/components/Wrappers/ControlledMultiSelect";
import { ControlledRadio } from "@/components/features/FormBuilder/components/Wrappers/ControlledRadio";
import { ControlledSelect } from "@/components/features/FormBuilder/components/Wrappers/ControlledSelect";
import { ControlledSignature } from "@/components/features/FormBuilder/components/Wrappers/Signature/ControlledSignature";
import { ControlledTimePicker } from "@/components/features/FormBuilder/components/Wrappers/ControlledTimePicker";
import { ControlledTextArea } from "@/components/features/FormBuilder/components/Wrappers/ControlledTextArea";
import { ControlledDateTimePicker } from "@/components/features/FormBuilder/components/Wrappers/ControlledDateTimePicker";
import type { ControlledFieldProps } from "@/components/features/FormBuilder/components/Wrappers/types";
import { FormErrorBox } from "@/components/ui/FormErrorBox/FormErrorBox";
import type { EvalResult } from "@/hooks/form-builder/usePreviewForm";
import styles from "@/components/features/FormBuilder/components/Preview/PreviewMode.module.scss";

const OPTION_COLLAPSE_THRESHOLD = 6;

function buildOptionData(question: FormQuestion) {
  return (question.options || [])
    .slice()
    .sort((a, b) => (a.value === "Other" ? 1 : b.value === "Other" ? -1 : 0))
    .map((o) => ({
      value: o.id,
      label: optionLabel(o.value),
    }));
}

interface QuestionFieldProps {
  question: FormQuestion;
  fieldProps: ControlledFieldProps;
  error?: string;
  onSig: (id: string) => void;
  isMobile?: boolean;
  logicContext?: FormLogicContext;
}

export function QuestionField({
  question,
  fieldProps,
  error,
  onSig,
  isMobile,
  logicContext,
}: QuestionFieldProps) {
  const { type } = question;
  const optionData = buildOptionData(question);
  const optionCount = (question.options || []).length;

  switch (type) {
    case "single-line-text":
    case "email":
    case "phone":
      return (
        <ControlledInput
          {...fieldProps}
          placeholder={question.placeholder || "Enter..."}
          iconLeft={type === "email" ? <IconMail size={20} /> : undefined}
        />
      );

    case "multi-line-text":
      return (
        <ControlledTextArea {...fieldProps} placeholder={question.placeholder || "Enter..."} />
      );

    case "number": {
      const unitPlacement = getUnitPlacement(question.unit);
      return (
        <Stack gap={0}>
          <Box
            className={`${styles.numberInputWrapper} ${error ? styles.numberInputWrapperError : ""}`}
          >
            {unitPlacement === "prefix" && (
              <Text size="sm" className={styles.unitText}>
                {question.unit}
              </Text>
            )}
            <Box className={styles.numberFieldGrow}>
              <ControlledInput
                {...fieldProps}
                error={undefined}
                placeholder={question.placeholder || "0"}
                onlyNumeric
              />
            </Box>
            {unitPlacement === "suffix" && (
              <Text size="sm" className={styles.unitText}>
                {question.unit}
              </Text>
            )}
          </Box>
          {error && <FormErrorBox message={error} mt="6" />}
        </Stack>
      );
    }

    case "date":
      return <ControlledDatePicker {...fieldProps} placeholder="MM / DD / YYYY" />;

    case "time":
      return <ControlledTimePicker {...fieldProps} placeholder="--:-- --" />;

    case "date-time":
      return <ControlledDateTimePicker {...fieldProps} />;

    case "radio":
      return optionCount <= OPTION_COLLAPSE_THRESHOLD ? (
        <ControlledRadio {...fieldProps} options={optionData} allowOther={question.allowOther} />
      ) : (
        <ControlledSelect {...fieldProps} data={optionData} allowOther={question.allowOther} />
      );

    case "checkbox":
      return optionCount <= OPTION_COLLAPSE_THRESHOLD ? (
        <ControlledCheckbox {...fieldProps} options={optionData} allowOther={question.allowOther} />
      ) : (
        <ControlledMultiSelect {...fieldProps} data={optionData} allowOther={question.allowOther} />
      );

    case "dropdown":
      return question.allowMultiple ? (
        <ControlledMultiSelect {...fieldProps} data={optionData} allowOther={question.allowOther} />
      ) : (
        <ControlledSelect {...fieldProps} data={optionData} allowOther={question.allowOther} />
      );

    case "signature":
      return (
        <ControlledSignature
          name={question.id}
          control={fieldProps.control}
          error={error}
          onSig={onSig}
          placeholder={question.placeholder}
        />
      );

    case "acknowledgement":
      return <ControlledAcknowledgement {...fieldProps} label={question.placeholder} />;

    case "photo-upload":
      return (
        <ControlledMediaCapture
          {...fieldProps}
          slots={question.photoSlots || [{ id: "1", label: "" }]}
          allowAdditional={question.allowAdditionalPhotos}
          mediaType="photo"
        />
      );

    case "video-upload":
      return (
        <ControlledMediaCapture
          {...fieldProps}
          slots={question.videoSlots || [{ id: "1", label: "" }]}
          allowAdditional={question.allowAdditionalVideos}
          mediaType="video"
        />
      );

    case "repeating-group":
      return (
        <ControlledRepeatingGroup
          {...fieldProps}
          group={question}
          isMobile={isMobile}
          logicContext={logicContext}
        />
      );

    default:
      return null;
  }
}

interface PreviewQuestionProps {
  question: FormQuestion;
  control: ControlledFieldProps["control"];
  evalResult: EvalResult;
  error?: string;
  onSig: (id: string) => void;
  clearErrors: (name?: string) => void;
  isMobile?: boolean;
}

export function PreviewQuestion({
  question,
  control,
  evalResult,
  error,
  onSig,
  clearErrors,
  isMobile,
}: PreviewQuestionProps) {
  const isRequired = isQuestionRequired(question, evalResult);
  const fieldProps: ControlledFieldProps = { name: question.id, control, error, clearErrors };
  const labelId = `${question.id}-label`;

  return (
    <Stack
      className={styles.previewQStack}
      data-question-id={question.id}
      role="group"
      aria-labelledby={labelId}
    >
      <Text
        id={labelId}
        size="sm"
        className={error ? styles.questionLabelError : styles.questionLabel}
      >
        {question.question || (
          <Text span c="var(--text-weak)" fw="var(--font-weight-medium)">
            {question.typeName} (no question yet)
          </Text>
        )}
        {isRequired && (
          <>
            <Text span c="red" className={styles.requiredMark} aria-hidden>
              *
            </Text>
            <VisuallyHidden> (required)</VisuallyHidden>
          </>
        )}
      </Text>
      {question.description && question.description !== " " && (
        <Text size="xs" className={styles.fieldDescription}>
          {question.description}
        </Text>
      )}
      <QuestionField
        question={question}
        fieldProps={fieldProps}
        error={error}
        onSig={onSig}
        isMobile={isMobile}
        logicContext={evalResult.context}
      />
      <VisuallyHidden role="alert">{error}</VisuallyHidden>
    </Stack>
  );
}
